import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
  Title2,
  Toaster,
  makeStyles,
  tokens,
  useId,
  useToastController,
  Toast,
  ToastTitle,
} from '@fluentui/react-components';
import {
  Add24Regular,
  ArrowClockwise24Regular,
  CheckmarkCircle24Regular,
  Copy24Regular,
  Delete24Regular,
  Edit24Regular,
  Key24Regular,
  PeopleCheckmark24Regular,
} from '@fluentui/react-icons';
import { useApi } from '../auth/useApi.ts';
import { useMe } from '../auth/useMe.ts';
import { useI18n } from '../i18n/context.tsx';

interface SharedKeyRow {
  id: string;
  parentKeyId: string;
  label: string;
  sharedAccessKey: string;
  virtualGroupId: string;
  maxRps: number;
  minIntervalMs: number;
  priority: number;
  allowedPathPrefixes: string[];
  allowedAdAccountIds: string[];
  enabled: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

/** Parse a newline/comma-separated text blob into an array of trimmed non-empty ids. */
function parseAdAccountIdList(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

interface NewCredentials {
  accessKey: string;
  secretKey: string;
}

interface ParentKeyRow {
  id: string;
  label: string;
  accessKey: string;
  enabled: boolean;
}

interface TestResult {
  total: number;
  passed: number;
  results: Array<{ id: string; ok: boolean; reason?: string }>;
  note?: string;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  form: { display: 'grid', gap: '12px', minWidth: '420px' },
  spinnerWrap: { padding: '40px' },
  secretBody: {
    display: 'grid',
    gap: '4px',
    marginTop: '12px',
    fontFamily: tokens.fontFamilyMonospace,
    wordBreak: 'break-all',
  },
});

export function SharedKeysPage() {
  const styles = useStyles();
  const qc = useQueryClient();
  const { request } = useApi();
  const { t } = useI18n();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';

  const sharedKeys = useQuery({
    queryKey: ['sharedKeys'],
    queryFn: () => request<SharedKeyRow[]>('/admin/shared-keys'),
  });
  const parentKeys = useQuery({
    queryKey: ['parentKeys'],
    queryFn: () => request<ParentKeyRow[]>('/admin/parent-keys'),
  });

  const [openCreate, setOpenCreate] = useState(false);
  const [newSecret, setNewSecret] = useState<NewCredentials | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SharedKeyRow | null>(null);
  const [editTarget, setEditTarget] = useState<SharedKeyRow | null>(null);
  const [editAccountsTarget, setEditAccountsTarget] = useState<SharedKeyRow | null>(null);
  const [secretInfoTarget, setSecretInfoTarget] = useState<SharedKeyRow | null>(null);
  const [testTarget, setTestTarget] = useState<SharedKeyRow | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const toggleEnabled = useMutation({
    mutationFn: (k: SharedKeyRow) =>
      request(`/admin/shared-keys/${k.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !k.enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sharedKeys'] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => request(`/admin/shared-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sharedKeys'] }),
  });

  const testAccess = useMutation({
    mutationFn: (k: SharedKeyRow) =>
      request<TestResult>(`/admin/shared-keys/${k.id}/test`, { method: 'POST' }),
    onSuccess: (data, k) => {
      setTestTarget(k);
      setTestResult(data);
    },
  });

  const editAccounts = useMutation({
    mutationFn: ({ id, allowedAdAccountIds }: { id: string; allowedAdAccountIds: string[] }) =>
      request(`/admin/shared-keys/${id}/accounts`, {
        method: 'PATCH',
        body: JSON.stringify({ allowedAdAccountIds }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sharedKeys'] }),
  });

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    dispatchToast(
      <Toast><ToastTitle>{t('common', 'copied')}</ToastTitle></Toast>,
      { intent: 'success' }
    );
  };

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.header}>
        <Title2>{t('sharedKeys', 'title')}</Title2>
        {isAdmin && (
          <Button appearance="primary" icon={<Add24Regular />} onClick={() => setOpenCreate(true)}>
            {t('common', 'add')}
          </Button>
        )}
      </div>

      <Card>
        {sharedKeys.isLoading ? (
          <div className={styles.spinnerWrap}><Spinner /></div>
        ) : (
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('common', 'label')}</TableHeaderCell>
                <TableHeaderCell>{t('sharedKeys', 'parentKey')}</TableHeaderCell>
                <TableHeaderCell>Virtual Group ID</TableHeaderCell>
                <TableHeaderCell>{t('sharedKeys', 'maxRps')}</TableHeaderCell>
                <TableHeaderCell>{t('sharedKeys', 'minInterval')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'priority')}</TableHeaderCell>
                <TableHeaderCell>{t('sharedKeys', 'adAccounts')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'enabled')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'lastUsed')}</TableHeaderCell>
                <TableHeaderCell>{t('common', 'actions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sharedKeys.data?.map((k) => (
                <TableRow key={k.id}>
                  <TableCell>{k.label}</TableCell>
                  <TableCell>
                    {parentKeys.data?.find((p) => p.id === k.parentKeyId)?.label ?? '-'}
                  </TableCell>
                  <TableCell>
                    <span className={styles.mono}>{k.virtualGroupId}</span>
                  </TableCell>
                  <TableCell>{k.maxRps}/s</TableCell>
                  <TableCell>{k.minIntervalMs} ms</TableCell>
                  <TableCell>
                    <Badge appearance="outline" color={k.priority >= 8 ? 'danger' : k.priority >= 5 ? 'warning' : 'informative'}>
                      {k.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {k.allowedAdAccountIds.length === 0 ? (
                      <span style={{ color: tokens.colorNeutralForeground3 }}>{t('sharedKeys', 'adAccountsAny')}</span>
                    ) : (
                      <span className={styles.mono} title={k.allowedAdAccountIds.join('\n')}>
                        {k.allowedAdAccountIds.length <= 2
                          ? k.allowedAdAccountIds.join(', ')
                          : `${k.allowedAdAccountIds.slice(0, 2).join(', ')} +${k.allowedAdAccountIds.length - 2}`}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isAdmin
                      ? <Switch checked={k.enabled} onChange={() => toggleEnabled.mutate(k)} />
                      : <span>{k.enabled ? '✓' : '✗'}</span>
                    }
                  </TableCell>
                  <TableCell>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    {isAdmin && (
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Edit24Regular />}
                        title={t('common', 'edit')}
                        onClick={() => setEditTarget(k)}
                      />
                    )}
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<PeopleCheckmark24Regular />}
                      title="編輯允許帳號"
                      onClick={() => setEditAccountsTarget(k)}
                    />
                    {k.allowedAdAccountIds.length > 0 && (
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={testAccess.isPending && testAccess.variables?.id === k.id
                          ? <Spinner size="tiny" />
                          : <CheckmarkCircle24Regular />}
                        title="Test Access"
                        disabled={testAccess.isPending && testAccess.variables?.id === k.id}
                        onClick={() => testAccess.mutate(k)}
                      />
                    )}
                    {isAdmin && (
                      <>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Key24Regular />}
                          title={t('sharedKeys', 'secretInfoTitle')}
                          onClick={() => setSecretInfoTarget(k)}
                        />
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Delete24Regular />}
                          title="delete"
                          onClick={() => setDeleteTarget(k)}
                        />
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateSharedKeyDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        parentKeys={parentKeys.data ?? []}
        onCreated={(s) => { setNewSecret(s); qc.invalidateQueries({ queryKey: ['sharedKeys'] }); }}
      />

      <EditSharedKeyDialog
        target={editTarget}
        isAdmin={isAdmin}
        onClose={() => setEditTarget(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ['sharedKeys'] })}
      />

      <EditAccountsDialog
        target={editAccountsTarget}
        onClose={() => setEditAccountsTarget(null)}
        onSave={(allowedAdAccountIds) => {
          if (editAccountsTarget) {
            editAccounts.mutate(
              { id: editAccountsTarget.id, allowedAdAccountIds },
              { onSuccess: () => setEditAccountsTarget(null) }
            );
          }
        }}
        isPending={editAccounts.isPending}
        isError={editAccounts.isError}
        error={editAccounts.error}
      />

      <SecretInfoDialog
        target={secretInfoTarget}
        onClose={() => setSecretInfoTarget(null)}
        onCopy={copy}
      />

      <Dialog open={newSecret !== null} onOpenChange={(_, d) => { if (!d.open) setNewSecret(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('sharedKeys', 'secretTitle')}</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <MessageBar intent="warning">
                  <MessageBarBody>{t('sharedKeys', 'secretBody')}</MessageBarBody>
                </MessageBar>
                {newSecret ? (
                  <>
                    <CredentialField label="Access Key" value={newSecret.accessKey} onCopy={copy} />
                    <CredentialField label="Secret Key" value={newSecret.secretKey} onCopy={copy} />
                  </>
                ) : null}
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewSecret(null)}>{t('common', 'close')}</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(_, d) => { if (!d.open) setDeleteTarget(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('common', 'confirmDeleteTitle')}</DialogTitle>
            <DialogContent>
              {deleteTarget
                ? t('sharedKeys', 'confirmDelete', { label: deleteTarget.label })
                : null}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('common', 'cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<Delete24Regular />}
                onClick={() => {
                  if (deleteTarget) del.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }}
              >
                {t('common', 'delete')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={testTarget !== null && testResult !== null}
        onOpenChange={(_, d) => { if (!d.open) { setTestTarget(null); setTestResult(null); } }}
      >
        <DialogSurface style={{ minWidth: '480px' }}>
          <DialogBody>
            <DialogTitle>測試結果 — {testTarget?.label}</DialogTitle>
            <DialogContent>
              {testResult && (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {testResult.note ? (
                    <MessageBar intent="info">
                      <MessageBarBody>{testResult.note}</MessageBarBody>
                    </MessageBar>
                  ) : (
                    <>
                      <div style={{
                        fontWeight: 600,
                        color: testResult.passed === testResult.total
                          ? tokens.colorPaletteLightGreenForeground1
                          : tokens.colorPaletteRedForeground1,
                      }}>
                        通過 {testResult.passed} / 共 {testResult.total} 個
                      </div>
                      <Table size="small">
                        <TableHeader>
                          <TableRow>
                            <TableHeaderCell>Ad Account ID</TableHeaderCell>
                            <TableHeaderCell>狀態</TableHeaderCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {testResult.results.map((r) => (
                            <TableRow key={r.id}>
                              <TableCell>
                                <span style={{ fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 }}>
                                  {r.id}
                                </span>
                              </TableCell>
                              <TableCell>
                                {r.ok ? (
                                  <span style={{ color: tokens.colorPaletteLightGreenForeground1 }}>✅ OK</span>
                                ) : (
                                  <span style={{ color: tokens.colorPaletteRedForeground1 }}>
                                    ❌ {r.reason ?? 'failed'}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => { setTestTarget(null); setTestResult(null); }}
              >
                {t('common', 'close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

function CreateSharedKeyDialog({
  open, onClose, parentKeys, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  parentKeys: ParentKeyRow[];
  onCreated: (s: NewCredentials) => void;
}) {
  const styles = useStyles();
  const { request } = useApi();
  const { t } = useI18n();
  const [label, setLabel] = useState('');
  const [parentKeyId, setParentKeyId] = useState('');
  const [maxRps, setMaxRps] = useState(5);
  const [minIntervalMs, setMinIntervalMs] = useState(0);
  const [priority, setPriority] = useState(5);
  const [adAccountIdsText, setAdAccountIdsText] = useState('');

  const create = useMutation({
    mutationFn: () =>
      request<{ id: string } & NewCredentials>('/admin/shared-keys', {
        method: 'POST',
        body: JSON.stringify({
          label,
          parentKeyId,
          maxRps,
          minIntervalMs,
          priority,
          allowedPathPrefixes: ['/api/'],
          allowedAdAccountIds: parseAdAccountIdList(adAccountIdsText),
        }),
      }),
    onSuccess: (data) => {
      onCreated({ accessKey: data.accessKey, secretKey: data.secretKey });
      onClose();
      setLabel('');
      setParentKeyId('');
      setMaxRps(5);
      setMinIntervalMs(0);
      setPriority(5);
      setAdAccountIdsText('');
    },
  });

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('sharedKeys', 'createTitle')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t('sharedKeys', 'labelHelp')}>
                <Input value={label} onChange={(_, d) => setLabel(d.value)} />
              </Field>
              <Field label={t('sharedKeys', 'parentHelp')}>
                <Dropdown
                  placeholder={t('sharedKeys', 'parentKey')}
                  value={parentKeys.find((p) => p.id === parentKeyId)?.label ?? ''}
                  selectedOptions={parentKeyId ? [parentKeyId] : []}
                  onOptionSelect={(_, d) => setParentKeyId(String(d.optionValue))}
                >
                  {parentKeys.map((p) => (
                    <Option key={p.id} value={p.id}>{p.label}</Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label={t('sharedKeys', 'maxRpsHelp')}>
                <Input type="number" value={String(maxRps)} onChange={(_, d) => setMaxRps(Number(d.value))} />
              </Field>
              <Field label={t('sharedKeys', 'minIntervalHelp')}>
                <Input type="number" value={String(minIntervalMs)} onChange={(_, d) => setMinIntervalMs(Number(d.value))} />
              </Field>
              <Field label={t('sharedKeys', 'priorityHelp')}>
                <Input type="number" value={String(priority)} onChange={(_, d) => setPriority(Number(d.value))} />
              </Field>
              <Field
                label={t('sharedKeys', 'adAccountsHelp')}
                hint={t('sharedKeys', 'adAccountsHint')}
              >
                <Textarea
                  rows={3}
                  value={adAccountIdsText}
                  onChange={(_, d) => setAdAccountIdsText(d.value)}
                  placeholder="A19685331107&#10;A19712345678"
                />
              </Field>
              {create.isError ? (
                <MessageBar intent="error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('common', 'createFailed')}</MessageBarTitle>
                    {(create.error as Error)?.message ?? String(create.error)}
                  </MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>{t('common', 'cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              disabled={!label || !parentKeyId || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common', 'create')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function EditSharedKeyDialog({
  target, isAdmin, onClose, onSaved,
}: {
  target: SharedKeyRow | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const styles = useStyles();
  const { request } = useApi();
  const { t } = useI18n();
  const [label, setLabel] = useState('');
  const [maxRps, setMaxRps] = useState(5);
  const [minIntervalMs, setMinIntervalMs] = useState(0);
  const [priority, setPriority] = useState(5);
  const [adAccountIdsText, setAdAccountIdsText] = useState('');

  useEffect(() => {
    if (target) {
      setLabel(target.label);
      setMaxRps(target.maxRps);
      setMinIntervalMs(target.minIntervalMs);
      setPriority(target.priority);
      setAdAccountIdsText(target.allowedAdAccountIds.join('\n'));
    }
  }, [target]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        allowedAdAccountIds: parseAdAccountIdList(adAccountIdsText),
      };
      // Admins can update all fields; viewers only update allowedAdAccountIds.
      if (isAdmin) {
        body.label = label;
        body.maxRps = maxRps;
        body.minIntervalMs = minIntervalMs;
        body.priority = priority;
      }
      return request(`/admin/shared-keys/${target!.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Dialog open={target !== null} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('sharedKeys', 'editTitle')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              {isAdmin && (
                <>
                  <Field label={t('sharedKeys', 'labelHelp')}>
                    <Input value={label} onChange={(_, d) => setLabel(d.value)} />
                  </Field>
                  <Field label={t('sharedKeys', 'maxRpsHelp')}>
                    <Input type="number" value={String(maxRps)} onChange={(_, d) => setMaxRps(Number(d.value))} />
                  </Field>
                  <Field label={t('sharedKeys', 'minIntervalHelp')}>
                    <Input type="number" value={String(minIntervalMs)} onChange={(_, d) => setMinIntervalMs(Number(d.value))} />
                  </Field>
                  <Field label={t('sharedKeys', 'priorityHelp')}>
                    <Input type="number" value={String(priority)} onChange={(_, d) => setPriority(Number(d.value))} />
                  </Field>
                </>
              )}
              <Field
                label={t('sharedKeys', 'adAccountsHelp')}
                hint={t('sharedKeys', 'adAccountsHint')}
              >
                <Textarea
                  rows={3}
                  value={adAccountIdsText}
                  onChange={(_, d) => setAdAccountIdsText(d.value)}
                  placeholder="A19685331107&#10;A19712345678"
                />
              </Field>
              {save.isError ? (
                <MessageBar intent="error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('common', 'saveFailed')}</MessageBarTitle>
                    {(save.error as Error)?.message ?? String(save.error)}
                  </MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>{t('common', 'cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              disabled={isAdmin ? !label : false}
              onClick={() => save.mutate()}
              aria-disabled={save.isPending}
            >
              {t('sharedKeys', 'save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function EditAccountsDialog({
  target, onClose, onSave, isPending, isError, error,
}: {
  target: SharedKeyRow | null;
  onClose: () => void;
  onSave: (allowedAdAccountIds: string[]) => void;
  isPending: boolean;
  isError: boolean;
  error: unknown;
}) {
  const styles = useStyles();
  const { t } = useI18n();
  const [adAccountIdsText, setAdAccountIdsText] = useState('');

  useEffect(() => {
    if (target) {
      setAdAccountIdsText(target.allowedAdAccountIds.join('\n'));
    }
  }, [target]);

  return (
    <Dialog open={target !== null} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>編輯允許帳號 — {target?.label}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field
                label={t('sharedKeys', 'adAccountsHelp')}
                hint={t('sharedKeys', 'adAccountsHint')}
              >
                <Textarea
                  rows={6}
                  value={adAccountIdsText}
                  onChange={(_, d) => setAdAccountIdsText(d.value)}
                  placeholder="A19685331107&#10;A19712345678"
                />
              </Field>
              {isError ? (
                <MessageBar intent="error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('common', 'saveFailed')}</MessageBarTitle>
                    {(error as Error)?.message ?? String(error)}
                  </MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose}>{t('common', 'cancel')}</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              disabled={isPending}
              onClick={() => onSave(parseAdAccountIdList(adAccountIdsText))}
            >
              {t('sharedKeys', 'save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface RotateResult {
  accessKey: string;
  secretKey: string;
}

function SecretInfoDialog({
  target, onClose, onCopy,
}: {
  target: SharedKeyRow | null;
  onClose: () => void;
  onCopy: (text: string) => void;
}) {
  const styles = useStyles();
  const { request } = useApi();
  const { t } = useI18n();
  const [fresh, setFresh] = useState<RotateResult | null>(null);

  // Clear any previously-shown secrets whenever the dialog closes or opens for a new row.
  useEffect(() => {
    if (!target) setFresh(null);
  }, [target]);

  const regen = useMutation({
    mutationFn: () =>
      request<RotateResult>(`/admin/shared-keys/${target!.id}/rotate`, { method: 'POST' }),
    onSuccess: (data) => setFresh(data),
  });

  return (
    <Dialog open={target !== null} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('sharedKeys', 'secretInfoTitle')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t('common', 'label')}>
                <Input readOnly value={target?.label ?? ''} />
              </Field>
              <Field label={t('sharedKeys', 'accessKey')}>
                <Input
                  readOnly
                  value={target?.sharedAccessKey ?? ''}
                  contentAfter={
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Copy24Regular />}
                      onClick={() => target && onCopy(target.sharedAccessKey)}
                    />
                  }
                />
              </Field>
              {fresh ? (
                <CredentialField label="Secret Key" value={fresh.secretKey} onCopy={onCopy} />
              ) : (
                <MessageBar intent="info">
                  <MessageBarBody>{t('sharedKeys', 'secretInfoNote')}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              icon={<ArrowClockwise24Regular />}
              disabled={regen.isPending}
              onClick={() => regen.mutate()}
            >
              {t('sharedKeys', 'regenerate')}
            </Button>
            <Button appearance="secondary" onClick={onClose}>{t('common', 'close')}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function CredentialField({
  label, value, onCopy,
}: {
  label: string;
  value: string;
  onCopy: (text: string) => void;
}) {
  return (
    <Field label={label}>
      <Input
        readOnly
        value={value}
        contentAfter={
          <Button
            appearance="subtle"
            size="small"
            icon={<Copy24Regular />}
            onClick={() => onCopy(value)}
          />
        }
      />
    </Field>
  );
}
